import { Checkbox } from 'src/components/ui/checkbox';
import { DEALER_ADMIN } from 'src/utils/enums/user-store-actions.enum';
import { IUser } from 'src/utils/interfaces/user';
import { RadioButtonOption } from 'src/components/ui/radioButton';
import { Types } from '../../dealer-security-maintenance';
import { useLocalization } from 'src/utils/useLocalization';
import React, { useCallback, useMemo, useState } from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';

import { DealerManagementService } from '../../../../../services/dealer-management.service';
import DialogModal from 'src/components/ui/dialogModal';
import Dropdown from 'src/components/ui/dropdownMenuTable/dropdownMenuTable';
import { HttpStatusCode } from '../../../../../utils/enums/http-method.enum';
import OptionsIcon from 'src/components/dealer-mgmt/options-icon';
import { useNavigate } from 'react-router-dom';

interface IPropsTable {
    items: Array<IPropsTableItem>;
    onClick: (name: string) => void;
    updateData: () => void;
    setIsSuccessDeactivate: (open: boolean) => void;
    setIsSuccessActivate: (open: boolean) => void;
    setIsSuccessDeleted: (open: boolean) => void;
    setIsSuccessDeletedMFA: (open: boolean) => void;
    setIsError: (open: boolean) => void;
    setIsErrorDGM: (open: boolean) => void;
}

interface IPropsTableItem {
    FIRSTNAME: string;
    LASTNAME: string;
    LOGIN: string;
    PARTYID: number;
    PERMISSIONEXISTS: number;
    STATUS: number;
    DEALERIDS: string;
    STATUSTEXT: string;
    ID: string;
}

export interface ITableItemProps {
    data: IPropsTableItem;
    selectedIds: Set<string>;
    onClick: (userId: string) => void;
    updateData: () => void;
    setIsSuccessDeactivate: (open: boolean) => void;
    setIsSuccessActivate: (open: boolean) => void;
    setIsSuccessDeleted: (open: boolean) => void;
    setIsSuccessDeletedMFA: (open: boolean) => void;
    setIsError: (open: boolean) => void;
    setIsErrorDGM: (open: boolean) => void;
    setIsLoadingDGM: (open: boolean) => void;
}

const MenuItem = ({
    userId,
    status,
    editHandler,
    dealerids,
    userName,
    deactivateHandler,
    activateHandler,
    deleteUserHandler,
    deleteMFAHandler,
}: {
    userId: string;
    status: string;
    dealerids?: string;
    editHandler: Function;
    userName: string;
    deactivateHandler: Function;
    activateHandler: Function;
    deleteUserHandler: Function;
    deleteMFAHandler: Function;
}) => {
    const { dealerMgmt } = useLocalization();
    const accessUser = useSelector((state: IUser) => {
        return state.accessUser;
    });
    return (
        <>
            <li
                className='menu-items'
                onClick={() =>
                    dealerids
                        ? editHandler(userName, dealerids)
                        : editHandler(userName)
                }
            >
                <span>{dealerMgmt.DEALER_MGMT_TABLE_MENU_EDIT}</span>
            </li>
            {!accessUser && (
                <>
                    <li
                        className='menu-items'
                        onClick={() => deleteMFAHandler(userName)}
                    >
                        <span>
                            {dealerMgmt.DEALER_MGMT_TABLE_MENU_DELETEMFA}
                        </span>
                    </li>
                    {status === 'Suspended' && (
                        <li
                            className='menu-items'
                            onClick={() => activateHandler(userId)}
                            data-testid={`table-item-activate-${userId}`}
                        >
                            <span>
                                {dealerMgmt.DEALER_MGMT_TABLE_MENU_ACTIVATE}
                            </span>
                        </li>
                    )}
                    {status === 'Active' && (
                        <li
                            className='menu-items'
                            onClick={() => deactivateHandler(userId)}
                            data-testid={`table-item-deactivate-${userId}`}
                        >
                            <span>
                                {dealerMgmt.DEALER_MGMT_TABLE_MENU_DEACTIVATE}
                            </span>
                        </li>
                    )}
                </>
            )}
            <li
                className='menu-items'
                onClick={() => {
                    deleteUserHandler();
                }}
            >
                <span>{dealerMgmt.DEALER_MGMT_TABLE_MENU_DELETE}</span>
            </li>
        </>
    );
};

export const TableItem = React.memo((props: ITableItemProps) => {
    const [open, setOpen] = useState(false);
    const { dealerMgmt, addNewUser } = useLocalization();
    const { data } = props;
    const dsmSearch = useSelector((s: IUser) => s.dsmSearch, shallowEqual);
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const fieldAccessMaintenance = useSelector(
        (s: IUser) => s.accessUser,
        shallowEqual,
    );

    const current = dsmSearch.selectedUsers ?? [];
    const isSelected = props.selectedIds.has(data.ID);

    const setSelectedUsers = useCallback(
        (selectedUsers: typeof current) => {
            dispatch({
                type: DEALER_ADMIN.DEALER_ADMIN_USER_ADD_SELECTED,
                payload: { selectedUsers },
            });
        },
        [dispatch],
    );

    const onSelectRadio = useCallback(() => {
        setSelectedUsers(isSelected ? [] : [data]);
    }, [setSelectedUsers, isSelected, data]);

    const onToggleCheckbox = useCallback(() => {
        if (isSelected) {
            setSelectedUsers(current.filter((u) => u.ID !== data.ID));
        } else {
            setSelectedUsers([...current, data]);
        }
    }, [setSelectedUsers, isSelected, current, data]);

    const isDealerNumber = dsmSearch.searchType === Types.DealerNumber;

    //
    const [rowUserId, setRowUserId] = useState<string>('');
    const [modalOpen, setModalOpen] = useState<string>('');
    const [modalIsLoading, setModalIsLoading] = useState<boolean>(false);

    const DEACTIVATED_STATUS = 1;
    const ACTIVATED_STATUS = 0;

    enum UserModalStatus {
        DELETE = 'DELETE',
        ACTIVATE = 'ACTIVATE',
        DEACTIVATE = 'DEACTIVATE',
        DELETEMFA = 'DELETEMFA',
    }

    const handleDeleteUserModal = () => {
        setModalOpen(UserModalStatus.DELETE);
    };
    const handleDeleteMFAModalOpen = () => {
        setModalOpen(UserModalStatus.DELETEMFA);
    };
    const activateDeactivateCall = async (status: number) => {
        const dealerManagementService = new DealerManagementService();
        try {
            return await dealerManagementService.updateUserStatus(
                rowUserId,
                status,
            );
        } catch (error) {
            console.error(error);
        }
    };
    const deleteUserCall = async () => {
        const dealerManagementService = new DealerManagementService();
        try {
            const response = await dealerManagementService.deleteUser(
                data.ID,
                data.LOGIN,
            );
            if (response.status !== HttpStatusCode.SUCCESS) {
                throw new Error('Error deleting the user');
            }
            return response;
        } catch (error) {
            console.error(error);
            throw error;
        }
    };
    const deleteMFACall = async () => {
        const dealerManagementService = new DealerManagementService();
        try {
            const response = await dealerManagementService.resetMfa(data.LOGIN);
            if (response.status !== HttpStatusCode.SUCCESS) {
                throw new Error('Error deleting the mfa');
            }
            return response;
        } catch (error) {
            console.error(error);
            throw error;
        }
    };

    const handleDeactivateModalOpen = (id: string) => {
        setRowUserId(id);
        setModalOpen(UserModalStatus.DEACTIVATE);
    };

    const handleActivateModalOpen = (id: string) => {
        setRowUserId(id);
        setModalOpen(UserModalStatus.ACTIVATE);
    };

    const handleModalCancel = () => {
        setModalOpen('');
    };

    const deactivateModalConfirm = () => {
        setModalIsLoading(true);
        activateDeactivateCall(DEACTIVATED_STATUS)
            .then(() => {
                props.setIsSuccessDeactivate(true);
                setModalOpen('');
                setModalIsLoading(false);
            })
            .finally(() => {
                props.updateData();
                setModalIsLoading(false);
                handleModalCancel();
            });
    };
    const activateModalConfirm = () => {
        setModalIsLoading(true);
        activateDeactivateCall(ACTIVATED_STATUS)
            .then(() => {
                props.setIsSuccessActivate(true);
                setModalOpen('');
                setModalIsLoading(false);
            })
            .finally(() => {
                props.updateData();
                setModalIsLoading(false);
                handleModalCancel();
            });
    };
    const deleteUserConfirm = () => {
        setModalIsLoading(true);
        deleteUserCall()
            .then(() => {
                props.setIsSuccessDeleted(true);
                setModalOpen('');
                setModalIsLoading(false);
                props.updateData();
            })
            .catch(() => {
                props.setIsError(true);
                setModalIsLoading(false);
            })
            .finally(() => {
                props.updateData();
                setModalIsLoading(false);
                handleModalCancel();
            });
    };
    const deleteMFAConfirm = () => {
        setModalIsLoading(true);
        deleteMFACall()
            .then(() => {
                props.setIsSuccessDeletedMFA(true);
                setModalOpen('');
                setModalIsLoading(false);
                props.updateData();
            })
            .catch(() => {
                props.setIsError(true);
                setModalIsLoading(false);
            })
            .finally(() => {
                props.updateData();
                setModalIsLoading(false);
                handleModalCancel();
            });
    };
    const getTitleContent = () => {
        let label;
        switch (modalOpen) {
            case UserModalStatus.DELETE: {
                label = `${dealerMgmt.DEALER_MGMT_DELETE_MODAL_TITLE} ${name}`;
                break;
            }
            case UserModalStatus.ACTIVATE: {
                label = dealerMgmt.DEALER_MGMT_TABLE_MENU_ACTIVATE;
                break;
            }
            case UserModalStatus.DEACTIVATE: {
                label = dealerMgmt.DEALER_MGMT_DEACTIVATE_MODAL_TITLE;
                break;
            }
            case UserModalStatus.DELETEMFA: {
                label = dealerMgmt.DEALER_MGMT_DELETEMFA_MODAL_TITLE;
                break;
            }
            default: {
                label = '';
                break;
            }
        }
        return label;
    };
    const getBodyContent = () => {
        let label;
        switch (modalOpen) {
            case UserModalStatus.DELETE: {
                label = dealerMgmt.DEALER_MGMT_DELETE_MODAL_DESCRIPTION;
                break;
            }
            case UserModalStatus.ACTIVATE: {
                label = dealerMgmt.DEALER_MGMT_ACTIVATE_MODAL_DESCRIPTION;
                break;
            }
            case UserModalStatus.DEACTIVATE: {
                label = dealerMgmt.DEALER_MGMT_DEACTIVATE_MODAL_DESCRIPTION;
                break;
            }
            case UserModalStatus.DELETEMFA: {
                label = dealerMgmt.DEALER_MGMT_DELETEMFA_MODAL_DESCRIPTION;
                break;
            }
            default: {
                label = '';
                break;
            }
        }
        return label;
    };
    const getConfirmBtnLabel = () => {
        let label;
        switch (modalOpen) {
            case UserModalStatus.DELETE: {
                label = dealerMgmt.DEALER_MGMT_DELETE_MODAL_CONFIRM_BUTTON;
                break;
            }
            case UserModalStatus.ACTIVATE: {
                label = dealerMgmt.DEALER_MGMT_ACTIVATE_MODAL_CONFIRM_BUTTON;
                break;
            }
            case UserModalStatus.DEACTIVATE: {
                label = dealerMgmt.DEALER_MGMT_DEACTIVATE_MODAL_CONFIRM_BUTTON;
                break;
            }
            case UserModalStatus.DELETEMFA: {
                label = dealerMgmt.DEALER_MGMT_DELETEMFA_MODAL_CONFIRM_BUTTON;
                break;
            }
            default: {
                label = '';
                break;
            }
        }
        return label;
    };

    const redirectToDealerDGM = async (dealerId: string) => {
        if (!dealerId) return;

        const service = new DealerManagementService();
        try {
            props.setIsLoadingDGM(true);
            props.setIsErrorDGM(false);
            const res = await service.getDealerGroups(dealerId);
            // eslint-disable-next-line no-magic-numbers
            if (Array.isArray(res.data) && res.data.length > 0) {
                const dealersGroupState = {
                    dealerGroups: res.data,
                    dealerGroupSearch: dealerId,
                };
                sessionStorage.setItem(
                    'dealerGroupState',
                    JSON.stringify(dealersGroupState),
                );
                navigate(
                    '/dashboard/systemadmin/dealer-group-maintenance/search',
                    {
                        state: dealersGroupState,
                        replace: true,
                    },
                );
            }
        } catch {
            props.setIsErrorDGM(true);
        } finally {
            props.setIsLoadingDGM(false);
        }
    };

    //
    return (
        <>
            <DialogModal
                titleContent={getTitleContent()}
                bodyContent={getBodyContent()}
                open={modalOpen !== ''}
                cancelBtnLabel={addNewUser.MODAL_LABEL_BUTTON_CANCEL}
                confirmBtnLabel={getConfirmBtnLabel()}
                onCancelHandler={handleModalCancel}
                onConfirmHandler={() => {
                    modalOpen === UserModalStatus.DELETE
                        ? deleteUserConfirm()
                        : modalOpen === UserModalStatus.ACTIVATE
                        ? activateModalConfirm()
                        : modalOpen === UserModalStatus.DEACTIVATE
                        ? deactivateModalConfirm()
                        : modalOpen === UserModalStatus.DELETEMFA
                        ? deleteMFAConfirm()
                        : null;
                }}
                isLoading={modalIsLoading}
                isConfirmBtnRed={modalOpen === UserModalStatus.DELETE}
            />
            <div className='sl-table-row'>
                <div className='table-row col-1'>
                    <span className='list-label'>Select</span>
                    <span>
                        {!isDealerNumber ? (
                            <RadioButtonOption
                                label=''
                                onSelect={onSelectRadio}
                                parentName='typeRadio'
                                option={data.ID}
                                selected={isSelected}
                            />
                        ) : (
                            <Checkbox
                                name=''
                                value={data.ID}
                                onCheck={onToggleCheckbox}
                                checked={isSelected}
                                allBlack={true}
                            />
                        )}
                    </span>
                </div>
                <div className='table-row col-2'>
                    <span className='list-label'>Name</span>
                    <span>
                        {data.FIRSTNAME} {data.LASTNAME}
                    </span>
                </div>

                <div className='table-row col-3'>
                    <span className='list-label'>Email</span>
                    <span>{data.LOGIN}</span>
                </div>

                <div className='table-row col-4'>
                    <span className='list-label'>Admin</span>
                    {data.PERMISSIONEXISTS === Number('1') ? (
                        <span className='c-badge__wrapper'>
                            <span className='c-badge badge'>Admin</span>
                        </span>
                    ) : null}
                </div>

                <div className='table-row col-5'>
                    <span className='list-label'>Status</span>
                    <span>
                        {data.STATUSTEXT === 'Active'
                            ? dealerMgmt.DEALER_MGMT_TABLE_USER_STATUS_ACTIVE
                            : dealerMgmt.DEALER_MGMT_TABLE_USER_STATUS_INACTIVE}
                    </span>
                </div>

                <div className='table-row col-6'>
                    <span className='list-label'>Dealers</span>

                    <span style={{ display: 'flex', flexWrap: 'wrap' }}>
                        {data.DEALERIDS.split(',')
                            .map((dealerId) => dealerId.trim())
                            .filter(Boolean)
                            .map((dealerId) =>
                                fieldAccessMaintenance ? (
                                    <span
                                        key={`dealer-${dealerId}`}
                                        style={{ marginRight: '5px' }}
                                    >
                                        {dealerId}
                                    </span>
                                ) : (
                                    <a
                                        key={`dealer-${dealerId}`}
                                        onClick={() =>
                                            redirectToDealerDGM(dealerId)
                                        }
                                        style={{ marginRight: '5px' }}
                                    >
                                        {dealerId}
                                    </a>
                                ),
                            )}
                    </span>
                </div>
                <div className='table-row col-7'>
                    <span className='list-label'></span>
                    <span
                        className='options-icon'
                        data-testid={`table-item-${data.ID}`}
                        onClick={() => {
                            setOpen(true);
                        }}
                    >
                        <OptionsIcon />
                    </span>
                    <Dropdown
                        dropdown={open}
                        showSelected={false}
                        showSelectedIcon={false}
                        handlerChange={(value: boolean) => {
                            setOpen(value);
                        }}
                    >
                        <MenuItem
                            userId={data.ID}
                            status={data.STATUSTEXT}
                            dealerids={data.DEALERIDS}
                            editHandler={props.onClick}
                            userName={data.LOGIN}
                            deleteUserHandler={handleDeleteUserModal}
                            deactivateHandler={handleDeactivateModalOpen}
                            activateHandler={handleActivateModalOpen}
                            deleteMFAHandler={handleDeleteMFAModalOpen}
                        />
                    </Dropdown>
                </div>
            </div>
        </>
    );
});

TableItem.displayName = 'TableItem';

export const Table = (props: IPropsTable) => {
    const { items } = props;
    const { dealerMgmt } = useLocalization();
    const dsmSearch = useSelector((s: IUser) => s.dsmSearch, shallowEqual);
    const isDealerNumber = dsmSearch.searchType === Types.DealerNumber;
    const selectedUsers = dsmSearch.selectedUsers ?? [];
    const allSelected =
        isDealerNumber &&
        items.length > Number('0') &&
        selectedUsers.length === items.length &&
        items.every((it) => selectedUsers.some((u) => u.ID === it.ID));

    const dispatch = useDispatch();
    const selectAll = () => {
        dispatch({
            type: DEALER_ADMIN.DEALER_ADMIN_USER_ADD_SELECTED,
            payload: { selectedUsers: allSelected ? [] : [...items] },
        });
    };

    const selectedIds = useMemo(() => {
        const ids = new Set<string>();
        for (const u of dsmSearch.selectedUsers ?? []) ids.add(u.ID);
        return ids;
    }, [dsmSearch.selectedUsers]);

    const [isLoadingDGM, setIsLoadingDGM] = useState<Boolean>(false);

    return (
        <div
            className={`sl-table ${
                isLoadingDGM ? 'c-spinner  c-spinner--section' : ''
            }`}
            aria-live='polite'
            aria-busy='true'
        >
            <div className='sl-table-row header-row hidden-in-list'>
                <div className='table-header col-1'>
                    <span className='u-text-truncate'>
                        {isDealerNumber ? (
                            <div
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                }}
                            >
                                <Checkbox
                                    name=''
                                    value={'all'}
                                    onCheck={selectAll}
                                    checked={allSelected}
                                    allBlack={true}
                                />
                                All
                            </div>
                        ) : (
                            'Select'
                        )}
                    </span>
                </div>
                <div className='table-header col-2'>
                    <span className='u-text-truncate'>
                        {dealerMgmt.DEALER_MGMT_TABLE_HEADER_NAME}
                    </span>
                </div>
                <div className='table-header col-3'>
                    <span className='u-text-truncate'>
                        {dealerMgmt.DEALER_MGMT_TABLE_HEADER_EMAIL}
                    </span>
                </div>
                <div className='table-header col-4'>
                    <span className='u-text-truncate'>
                        {dealerMgmt.DEALER_MGMT_TABLE_HEADER_ADMIN}
                    </span>
                </div>
                <div className='table-header col-5'>
                    <span className='u-text-truncate'>
                        {dealerMgmt.DEALER_MGMT_TABLE_HEADER_STATUS}
                    </span>
                </div>
                <div className='table-header col-6'>
                    <span className='u-text-truncate'>
                        {dealerMgmt.DEALER_MGMT_TABLE_HEADER_DEALERS}
                    </span>
                </div>
                <div className='table-header col-7 menu-header'></div>
            </div>
            {items.map((item) => {
                return (
                    <TableItem
                        key={Math.random() + item.ID}
                        data={item}
                        selectedIds={selectedIds}
                        onClick={props.onClick}
                        updateData={props.updateData}
                        setIsSuccessDeleted={props.setIsSuccessDeleted}
                        setIsSuccessDeactivate={props.setIsSuccessDeactivate}
                        setIsSuccessActivate={props.setIsSuccessActivate}
                        setIsSuccessDeletedMFA={props.setIsSuccessDeletedMFA}
                        setIsError={props.setIsError}
                        setIsErrorDGM={props.setIsErrorDGM}
                        setIsLoadingDGM={setIsLoadingDGM}
                    />
                );
            })}
            {isLoadingDGM && (
                <div className='c-spinner__loader'>
                    <span className='c-spinner__element'></span>
                    <span className='c-spinner__element'></span>
                    <span className='c-spinner__element'></span>
                    <span className='c-spinner__element'></span>
                    <span className='c-spinner__element'></span>
                    <span className='c-spinner__element'></span>
                    <span className='c-spinner__element'></span>
                    <span className='c-spinner__element'></span>
                </div>
            )}
        </div>
    );
};
